/**
 * Fixing a food that other people scan.
 *
 * `food_items` is shared, so this is a data-integrity question wearing a form's
 * clothes. The backend has always split by source — a barcode row is objective,
 * so a fix to it is a fix for everyone, while a private guess forks — and the UI
 * never mentioned it. The rule was applied silently and correctly, which is the
 * worst arrangement available: somebody who meant "only my copy" and got
 * "everyone's" had no way to discover it until a stranger disagreed with their
 * figures.
 *
 * So the choice is made explicit, once, in plain words, **between the numbers
 * and the save** — where it cannot be skipped past. Putting it above the fields
 * would make it a setting nobody reads; putting it after the button would make
 * it a confirmation dialogue, which people click through.
 *
 * The source still supplies the default, because it is a good guess. It is only
 * a guess that now has an override.
 */

import {
  figureChange,
  figureMoved,
  figureValue,
  seedFigure,
  NUTRIENTS,
  useCorrections,
  useCorrectFood,
  type CorrectionScope,
  type FigureSpec,
  type FoodItem,
} from "@calorio/core";
import { useState } from "react";

import { OptionalDetail } from "./OptionalDetail";
import { useCalorio } from "../app/providers";

interface Field extends FigureSpec {
  key: keyof FoodItem & string;
  label: string;
}

/** The four every food has, and which it cannot be logged without. */
const CORE: readonly Field[] = [
  { key: "kcal_per_100", label: "kcal / 100 g", required: true },
  { key: "protein_g_per_100", label: "Protein g", required: true },
  { key: "carbs_g_per_100", label: "Carbs g", required: true },
  { key: "fat_g_per_100", label: "Fat g", required: true },
];

/**
 * The four a packet may or may not print.
 *
 * Not `required`, so clearing one is a deliberate act that sends `null` — the
 * right answer when a figure turns out to have been wrong and nobody knows the
 * real one. Salt carries the conversion: stored as sodium in milligrams, typed
 * in grams, compared in grams.
 */
const DETAIL: readonly Field[] = NUTRIENTS.map((spec) => ({
  key: spec.per100Key,
  label: `${spec.label} ${spec.unit}`,
  decimals: spec.decimals,
  display: spec.display,
  store: spec.store,
}));

const FIELDS: readonly Field[] = [...CORE, ...DETAIL];

/** Which sources carry a truth printed on a packet that anyone can check. */
const OBJECTIVE = new Set(["off", "label_ocr"]);

interface CorrectFoodProps {
  food: FoodItem;
  /**
   * Reached from a logged row rather than from the product.
   *
   * Changes the default only. Somebody fixing an entry they are looking at is
   * usually saying "my portion was different", not "the database is wrong".
   */
  fromEntry?: boolean;
  onDone: () => void;
  onCancel: () => void;
}

export function CorrectFood({ food, fromEntry = false, onDone, onCancel }: CorrectFoodProps) {
  const ctx = useCalorio();
  const correct = useCorrectFood(ctx);
  const history = useCorrections(ctx, food.id);

  const shareable = OBJECTIVE.has(food.source) && food.shared;
  const [scope, setScope] = useState<CorrectionScope>(
    shareable && !fromEntry ? "product" : "mine",
  );

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(FIELDS.map((f) => [f.key, seedFigure(food[f.key] as number | null, f)])),
  );
  const [problem, setProblem] = useState<string | null>(null);

  // Three-way, not two. A field that was blank and stayed blank is untouched;
  // one that was blanked is a deliberate clear and sends `null`. Comparing a
  // blank against `Number(null ?? 0)` reported every unset figure as edited the
  // moment this panel opened, and sent `NaN` — which is `null` on the wire, and
  // clears the figure for everyone who scans that barcode.
  const changesByKey = new Map(
    FIELDS.map((f) => [f.key, figureChange(values[f.key], food[f.key] as number | null, f)]),
  );
  const edited = FIELDS.filter((f) => figureMoved(changesByKey.get(f.key)!));

  const save = async () => {
    setProblem(null);
    if (FIELDS.some((f) => changesByKey.get(f.key)!.state === "invalid")) {
      return setProblem("Every figure has to be a number, and none of them can be negative.");
    }
    const changes = Object.fromEntries(
      edited.map((f) => [f.key, figureValue(changesByKey.get(f.key)!)]),
    );
    if (!Object.keys(changes).length) return onCancel();

    try {
      await correct.mutateAsync({ id: food.id, changes, scope });
      onDone();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Couldn't save that — try again.");
    }
  };

  const box = (field: Field) => {
    const changed = edited.some((e) => e.key === field.key);
    return (
      <label className="correct__field" key={field.key}>
        <span>{field.label}</span>
        <input
          className={`field num correct__value${changed ? " is-edited" : ""}`}
          inputMode="decimal"
          value={values[field.key] ?? ""}
          /* An em dash on the optional four, never a 0 — a placeholder zero in
             a box that may legitimately stay empty reads as the answer. */
          placeholder={field.required ? undefined : "—"}
          onChange={(event) =>
            setValues((current) => ({ ...current, [field.key]: event.target.value }))
          }
          aria-label={field.label}
        />
      </label>
    );
  };

  return (
    <div className="correct">
      <h2 className="correct__name">{food.brand ? `${food.brand} ${food.name}` : food.name}</h2>
      <p className="num correct__meta">
        {food.barcode && <>barcode {food.barcode} · </>}
        {food.source === "off"
          ? "from Open Food Facts"
          : food.source === "label_ocr"
            ? "read off the label"
            : food.source === "generic"
              ? "USDA reference data"
              : "entered by hand"}
      </p>

      <div className="correct__grid">{CORE.map(box)}</div>

      {/* Open when this food already carries one of them: hiding a value that
          exists is worse than the height, while hiding four empty boxes is the
          whole point of the toggle. */}
      <OptionalDetail defaultOpen={DETAIL.some((f) => food[f.key] !== null)}>
        <div className="correct__grid">{DETAIL.map(box)}</div>
      </OptionalDetail>

      {/* Only where there is a shared row to change. Offering "correct the
          product" on a food nobody else can see would be offering a choice with
          one real option, and the server refuses it anyway. */}
      {shareable && (
        <div className="correct__scope">
          <span className="correct__scope-label">Who gets this fix</span>

          <button
            className={`correct__option${scope === "product" ? " is-on" : ""}`}
            onClick={() => setScope("product")}
            aria-pressed={scope === "product"}
          >
            <span className="correct__radio" aria-hidden="true" />
            <span>
              <span className="correct__option-title">Correct the product</span>
              <span className="correct__option-sub">
                The label is wrong for everyone, and the next person to scan this barcode gets your
                figures. Recorded against your account.
              </span>
            </span>
          </button>

          <button
            className={`correct__option${scope === "mine" ? " is-on" : ""}`}
            onClick={() => setScope("mine")}
            aria-pressed={scope === "mine"}
          >
            <span className="correct__radio" aria-hidden="true" />
            <span>
              <span className="correct__option-title">Only my copy</span>
              <span className="correct__option-sub">
                The product is fine; you weighed a different thing. Forks a private food; the
                shared row is untouched.
              </span>
            </span>
          </button>
        </div>
      )}

      {/* The reassurance that makes either choice safe to make. Without it,
          "correct the product" reads as though it might rewrite your diary. */}
      <p className="manual__note">
        Either way, meals you already logged keep the macros they were logged with — editing a food
        never rewrites history.
      </p>

      {problem && (
        <p className="error" style={{ margin: 0 }}>
          {problem}
        </p>
      )}

      <div className="correct__actions">
        <button className="correct__save" onClick={() => void save()} disabled={correct.isPending}>
          {correct.isPending ? "Saving…" : "Save correction"}
        </button>
        <button className="correct__cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>

      {/* A row anyone can edit has to be a row anyone can inspect. */}
      {history.data && history.data.length > 0 && (
        <div className="correct__history">
          <span className="correct__scope-label">Corrections on this product</span>
          {history.data.map((entry) => (
            <div className="correct__history-row" key={entry.id}>
              <span>
                {entry.changes
                  .map((c) => `${c.field.replace(/_.*$/, "")} ${c.before ?? "—"} → ${c.after ?? "—"}`)
                  .join(", ")}
              </span>
              <span className="num correct__history-by">
                {entry.by_you ? "you" : "someone else"} ·{" "}
                {new Date(entry.at).toLocaleDateString(undefined, {
                  day: "numeric",
                  month: "short",
                })}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
